<template>
  <div class="ovulation-calculator">
    <div class="ovulation-settings">
      <div class="ovulation-settings__calendar">
        <p class="ovulation-settings__title">Дата начала последней менструации</p>
        <BaseCalendar
          :date="date"
          primary-color="#b9356f"
          hover-color="#a73064"
          hover-color-popover="rgba(185, 53, 111, 0.1)"
          class="ovulation-settings__calendar-select"
          @update-date="date = $event"
        />
      </div>
      <div class="ovulation-settings__period">
        <p class="ovulation-settings__title">Продолжительность цикла</p>
        <client-only>
          <BaseDropDown
            :default-option="selectPeriod"
            :options="options"
            class="ovulation-settings__period-select"
            @change-option="selectPeriod = $event"
          />
        </client-only>
      </div>
    </div>
    <div class="ovulation-calculator-popup">
      <p
        class="ovulation-calculator-popup__text"
        @click="changeStatePopup(!showPopup)"
      >
        У меня другая продолжительность
        <svg
          class="ovulation-calculator-popup__image"
          :class="{ 'ovulation-calculator-popup__image-rotate': showPopup }"
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="10"
          viewBox="0 0 16 10"
          fill="none"
        >
          <path
            d="M14.0925 1.5456L7.54688 8.0912L1.00127 1.5456"
            stroke="#B9356F"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
      </p>
      <Transition name="fade">
        <div
          v-if="showPopup"
          class="ovulation-calculator-popup__description"
        >
          <p class="ovulation-calculator-popup__description-text">
            У большинства женщин цикл составляет 28 дней, но допускаются колебания от 21 до 35 дней.
            Если ваша средняя продолжительность цикла не укладывается в этот диапазон, мы не сможем
            рассчитать дату овуляции. Чтобы получить корректную информацию, рекомендуем обратиться к
            гинекологу.
          </p>
        </div>
      </Transition>
    </div>
    <BaseButton
      class="ovulation-calculator__btn"
      :text="isFirstCount ? 'Рассчитать' : 'Рассчитать заново'"
      :disabled="!date"
      @click="
        ((showResult = true), (isFirstCount = false), calcDateOvulaton(), calcDateFertility())
      "
    />
    <div
      v-if="showResult"
      class="ovulation-result"
    >
      <div class="ovulation-result-container">
        <p class="ovulation-result__title">Дата овуляции</p>
        <p
          class="ovulation-result__text"
          v-html="ovulationText"
        />
      </div>
      <div class="ovulation-result__separator" />
      <div class="ovulation-result-container">
        <p class="ovulation-result__title">Окно фертильности</p>
        <p
          class="ovulation-result__text"
          v-html="fertilityText"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import { getAddDaysToDate } from '@devhacker/shared/utils/date';
  import BaseButton from '~/components/BaseButton.vue';
  import BaseDropDown from '~/components/BaseDropDown.vue';
  import BaseCalendar from '~/components/BaseCalendar.vue';
  import Transition from '~/components/transition.vue';

  const months: string[] = [
    'января',
    'февраля',
    'марта',
    'апреля',
    'мая',
    'июня',
    'июля',
    'августа',
    'сентября',
    'октября',
    'ноября',
    'декабря',
  ];

  export default defineNuxtComponent({
    name: 'OvulationCalculator',

    components: {
      BaseButton,
      BaseDropDown,
      Transition,
      BaseCalendar,
    },

    data() {
      return {
        selectPeriod: '28',
        options: [
          '21',
          '22',
          '23',
          '24',
          '25',
          '26',
          '27',
          '28',
          '29',
          '30',
          '31',
          '32',
          '33',
          '34',
          '35',
        ],
        showPopup: false,
        showResult: false,
        isFirstCount: true,
        date: null,
        ovulationText: '',
        fertilityText: '',
      };
    },

    methods: {
      changeStatePopup(state: boolean = false) {
        this.showPopup = state;
      },
      handleClickOutside() {
        this.showPopup = false;
      },
      calcDateOvulaton() {
        if (this.date) {
          // @ts-ignore
          const dateOvulation = getAddDaysToDate(this.date, Number(this.selectPeriod) - 14);
          this.ovulationText =
            dateOvulation.getDate() + '&nbsp;' + months[dateOvulation.getMonth()];
        }
      },
      calcDateFertility() {
        if (this.date) {
          // @ts-ignore
          const startPeriod = getAddDaysToDate(this.date, Number(this.selectPeriod) - 14 - 4);
          // @ts-ignore
          const endPeriod = getAddDaysToDate(this.date, Number(this.selectPeriod) - 14 + 1);
          this.fertilityText = `${startPeriod.getDate()}&nbsp;${months[startPeriod.getMonth()]} — ${endPeriod.getDate()}&nbsp;${months[endPeriod.getMonth()]}`;
        }
      },
    },
  });
</script>

<style lang="scss" scoped>
  $defaultColor: #b9356f;
  $hoverColor: #a73064;
  $bg: #fceff2;

  .ovulation-calculator {
    padding: 32px 16px;
    margin: 0 -16px 12px;
    @include tablet {
      margin: 0 0 12px;
      padding: 32px 24px;
    }
    @include big-tablet {
      margin: 0 -24px 12px;
      padding: 32px 24px;
    }
    background-color: $bg;

    &__btn {
      margin-top: 16px;
    }

    :deep() {
      .base-button {
        position: relative;
        background: $defaultColor;
        @include hover {
          background: $hoverColor;
        }
        &:disabled {
          opacity: 0.3;
        }
      }
      .dropdown {
        @include hover {
          border: 1px solid $defaultColor;
          svg {
            path {
              stroke: $defaultColor;
            }
          }
        }
        &--active {
          border: 1px solid $defaultColor;
          svg {
            path {
              stroke: $defaultColor;
            }
          }
        }
        &__options {
          li {
            @include hover {
              background-color: rgba(185, 53, 111, 0.1);
            }
          }
        }
      }
    }

    .ovulation-calculator-popup {
      margin-top: 8px;

      &__text {
        width: max-content;
        color: $defaultColor;
        font-family: $mainFont;
        font-size: 16px;
        line-height: 24px;
        font-weight: 700;
        height: 40px;
        position: relative;
        display: flex;
        align-items: center;
        svg {
          margin-left: 8px;
          margin-top: 2px;
          @include desktop {
            margin-left: 14px;
          }
        }
        @include hover {
          cursor: pointer;
          color: $hoverColor;
          svg {
            path {
              stroke: $hoverColor;
            }
          }
        }
      }

      &__description {
        background: #fff;
        padding: 16px;
        margin-top: 12px;
        border-radius: 4px;
        &-text {
          font-weight: 400;
          font-size: 16px;
          line-height: 24px;
          font-family: $mainFont;
        }
      }

      &__image {
        transition: transform 0.5s;
        transform: rotate(0deg);
        &-rotate {
          transform: rotate(180deg);
        }
      }
    }
  }

  .ovulation-settings {
    @include desktop {
      display: flex;
    }

    &__title {
      color: #000;
      white-space: nowrap;
      font-weight: 600;
      font-size: 16px;
      line-height: 24px;
      font-family: $secondFont;
    }

    &__calendar,
    &__period {
      @include desktop {
        width: 50%;
      }
    }
    &__calendar {
      &-select {
        margin-top: 8px;
      }
      @include desktop {
        margin-top: 0;
      }
    }
    &__period {
      margin-top: 16px;
      @include desktop {
        margin-top: 0;
        margin-left: 16px;
      }
      &-select {
        margin-top: 8px;
      }
    }
  }

  .ovulation-result {
    background: #fff;
    border-top: 2px solid $defaultColor;
    padding: 24px 16px;
    margin: 24px -16px 0;
    @include tablet {
      margin: 24px 0 0;
    }
    @include desktop {
      padding: 24px;
    }
    &__separator {
      width: 100%;
      background-color: #e7e7e7;
      height: 1px;
      margin: 16px 0;
    }

    &__title {
      font-weight: 600;
      color: #000;
      font-size: 16px;
      line-height: 24px;
      font-family: $secondFont;
    }
    &__text {
      margin-top: 4px;
      font-weight: 400;
      line-height: 24px;
      font-size: 16px;
      font-family: $mainFont;
    }
  }
</style>
